import {Contact as TucContact} from '@hconnect/tuc-processes/types'

import {api} from '../../App.global'
import {DistributedCacheResponse} from '../../DistributedStorage/type'
import {RmcOrderPayload} from '../../features/rmc/orderIntake/services/rmcOrderPlacementService'
import {
  OrderChangeResponse,
  SubmitOrderChangeParams
} from '../../features/rmc/orderManagement/types/orderChange.types'
import {CancellationRequestStatus} from '../../features/rmc/orderCancellation/types/cancellationRequest.types'
import {
  ReserveSlotRequest,
  ReserveSlotResponse,
  ReleaseSlotRequest
} from '../../OrderIntake/BulkCement/containers/slotManagement/types'
import {
  SplitLoadOrderIntakePayload,
  V2SplitLoadOrderIntakePayload
} from '../../OrderIntake/BulkCement/containers/SplitLoad/buildSplitLoadPayload'
import {
  Contact,
  NotificationSettings,
  OrderCancellation,
  OrderIntakePayload,
  OrderRequestResponse,
  PersistedOrderRequest,
  RequestForQuoteResponse,
  V2OrderIntakePayload
} from '../../OrderIntake/declarations/types'
import {
  PersistedRequestForQuoteFormValues,
  RequestForQuotePayload
} from '../../OrderIntake/RequestForQuoteFormValues'
import {
  toPersistedRequestForQuote,
  toRequestForQuotePayload
} from '../../OrderIntake/BulkCement/RequestForQuote/requestForQuoteMapper'
import {
  ORDER_REQUESTS_API_PATH,
  ORDERS_MANAGEMENT_API_PATH,
  REQUEST_FOR_QUOTES_API_PATH,
  V2_ORDER_REQUESTS_API_PATH
} from '../../Organisms/OrderIntake/OrderIntake.action'
import {FilterDefinition} from '../types'

import {AsyncFn} from './api'
import {queries} from './queries'

export interface Mutations {
  postOrderRequestDelivery: AsyncFn<
    [
      {
        orderIntakePayload: OrderIntakePayload | SplitLoadOrderIntakePayload
        isConfigurableSlots: boolean
        isAutoConfirmed: boolean
      }
    ],
    OrderRequestResponse
  >
  postOrderRequestDeliveryV2: AsyncFn<
    [
      {
        orderIntakePayload: V2OrderIntakePayload | V2SplitLoadOrderIntakePayload
        apiPath?: string
      }
    ],
    OrderRequestResponse
  >
  postOrderRequestCollection: AsyncFn<
    [{orderIntakePayload: OrderIntakePayload | SplitLoadOrderIntakePayload}],
    OrderRequestResponse
  >
  postOrderRequestCollectionV2: AsyncFn<
    [{orderIntakePayload: V2OrderIntakePayload | V2SplitLoadOrderIntakePayload; apiPath?: string}],
    OrderRequestResponse
  >
  patchOrderRequest: AsyncFn<[{requestId: string}], OrderRequestResponse>
  persistOrderRequests: AsyncFn<
    [{siteNumber: string; orderRequests: PersistedOrderRequest[]}],
    PersistedOrderRequest
  >
  persistContact: AsyncFn<[{siteNumber: string; contact: Contact}], Contact>
  persistRmcOrder: AsyncFn<
    [{siteNumber: string; rmcOrderPayloads: RmcOrderPayload[]}],
    RmcOrderPayload[]
  >
  persistRmcContact: AsyncFn<
    [{siteNumber: string; rmcContact: {orderPlacer?: TucContact; siteContact?: TucContact}}],
    {orderPlacer?: TucContact; siteContact?: TucContact}
  >
  postRequestForQuote: AsyncFn<[{payload: RequestForQuotePayload}], RequestForQuoteResponse>
  cancelOrderRequest: AsyncFn<
    [{orderId: string; cancelOrderPayload: OrderCancellation}],
    CancellationRequestStatus
  >
  submitOrderChange: AsyncFn<[SubmitOrderChangeParams], OrderChangeResponse>
  persistRequestForQuote: AsyncFn<
    [{siteNumber: string; businessLine: string; formValues: PersistedRequestForQuoteFormValues}],
    PersistedRequestForQuoteFormValues
  >
  postNotificationSettings: AsyncFn<
    [{notificationSettings: NotificationSettings}],
    NotificationSettings
  >
  persistFilters: AsyncFn<
    [{filterType: string; filters: FilterDefinition[]}],
    PersistedRequestForQuoteFormValues
  >
  deleteScheduledExports: AsyncFn<[{scheduledExportId: string}], boolean>
  postSlotReservation: AsyncFn<[ReserveSlotRequest], ReserveSlotResponse>
  postSlotRelease: AsyncFn<[ReleaseSlotRequest], boolean>
  dismissOrderChangeBanner: AsyncFn<[{requestId: string}], string[]>
  dismissOrderCancellationBanner: AsyncFn<[{requestId: string}], string[]>
  dismissFailedOrderChangeRequest: AsyncFn<[{requestId: string}], string[]>
  validateConfirmationPin: AsyncFn<
    [{customerId: string; pin: string}],
    ValidateConfirmationPinResponse
  >
}

export interface ValidateConfirmationPinResponse {
  result: 'valid' | 'invalid'
}

export const mutations: Mutations = {
  postOrderRequestDelivery: async ({orderIntakePayload, isConfigurableSlots, isAutoConfirmed}) => {
    const response = await api.post<OrderRequestResponse>(
      ORDER_REQUESTS_API_PATH,
      orderIntakePayload,
      {
        params: {
          isConfigurableSlots: isConfigurableSlots,
          isAutoConfirmed: isAutoConfirmed
        }
      }
    )
    return response.data
  },
  postOrderRequestDeliveryV2: async ({orderIntakePayload, apiPath}) => {
    const basePath = apiPath ?? V2_ORDER_REQUESTS_API_PATH
    const response = await api.post<OrderRequestResponse>(basePath, orderIntakePayload)
    return response.data
  },
  postOrderRequestCollection: async ({orderIntakePayload}) => {
    const response = await api.post<OrderRequestResponse>(
      ORDER_REQUESTS_API_PATH,
      orderIntakePayload
    )
    return response.data
  },
  postOrderRequestCollectionV2: async ({orderIntakePayload, apiPath}) => {
    const basePath = apiPath ?? V2_ORDER_REQUESTS_API_PATH
    const response = await api.post<OrderRequestResponse>(basePath, orderIntakePayload)
    return response.data
  },
  patchOrderRequest: async ({requestId}) => {
    const response = await api.patch<OrderRequestResponse>(
      `${ORDER_REQUESTS_API_PATH}/${requestId}`
    )
    return response.data
  },
  persistOrderRequests: async ({siteNumber, orderRequests}) => {
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: `orderIntake-orderRequests-${siteNumber}`,
      value: JSON.stringify(orderRequests)
    })
    return JSON.parse(response.data.value)
  },
  persistContact: async ({siteNumber, contact}) => {
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: `orderIntake-contact-${siteNumber}`,
      value: JSON.stringify(contact)
    })
    return JSON.parse(response.data.value)
  },
  persistRmcOrder: async ({siteNumber, rmcOrderPayloads}) => {
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: `orderIntake-orderRequests-${siteNumber}-RMC`,
      value: JSON.stringify(rmcOrderPayloads)
    })
    return JSON.parse(response.data.value)
  },
  persistRmcContact: async ({siteNumber, rmcContact}) => {
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: `orderIntake-contact-${siteNumber}-RMC`,
      value: JSON.stringify(rmcContact)
    })
    return JSON.parse(response.data.value)
  },
  postRequestForQuote: async ({payload}) => {
    const response = await api.post<RequestForQuoteResponse>(
      REQUEST_FOR_QUOTES_API_PATH,
      toRequestForQuotePayload(payload, payload)
    )
    return response.data
  },
  cancelOrderRequest: async ({orderId, cancelOrderPayload}) => {
    const response = await api.post<CancellationRequestStatus>(
      `${ORDERS_MANAGEMENT_API_PATH}/Cancel/${orderId}`,
      cancelOrderPayload
    )
    return response.data
  },
  submitOrderChange: async ({orderId, payload}) => {
    const response = await api.patch<OrderChangeResponse>(
      `${ORDERS_MANAGEMENT_API_PATH}/v2/Change/${orderId}`,
      payload
    )
    return response.data
  },
  persistRequestForQuote: async ({siteNumber, businessLine, formValues}) => {
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: `orderIntake-request-for-quote-${siteNumber}-${businessLine}`,
      value: JSON.stringify(toPersistedRequestForQuote(formValues))
    })
    return toPersistedRequestForQuote(JSON.parse(response.data.value))
  },
  postNotificationSettings: async ({notificationSettings}) => {
    const response = await api.put<NotificationSettings>(
      '/user-subscriptions',
      notificationSettings
    )
    return response.data
  },
  persistFilters: async ({filterType, filters}) => {
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: `orderIntake-filters-${filterType}`,
      value: JSON.stringify(filters)
    })
    return JSON.parse(response.data.value)
  },
  deleteScheduledExports: async ({scheduledExportId}) => {
    await api.delete<boolean>(`scheduledExports/${scheduledExportId}`)
    return true
  },
  postSlotReservation: async (slotReservationRequest: ReserveSlotRequest) => {
    const response = await api.post<ReserveSlotResponse>(
      '/slotmanagement/reserve-slot',
      slotReservationRequest
    )
    return response.data
  },
  postSlotRelease: async (slotReleaseRequest: ReleaseSlotRequest) => {
    const response = await api.post<boolean>('/slotmanagement/release-slot', slotReleaseRequest)
    return response.data
  },
  dismissOrderChangeBanner: async ({requestId}) => {
    // Reuse the query to get current dismissed banners
    const dismissedBanners = await queries.dismissedOrderChangeBanners()

    // Add the new requestId if not already present
    if (!dismissedBanners.includes(requestId)) {
      dismissedBanners.push(requestId)
    }

    // Persist the updated list
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: 'orderChange-dismissed-banners',
      value: JSON.stringify(dismissedBanners)
    })

    return JSON.parse(response.data.value)
  },
  dismissOrderCancellationBanner: async ({requestId}) => {
    const dismissedBanners = await queries.dismissedOrderCancellationBanners()

    if (!dismissedBanners.includes(requestId)) {
      dismissedBanners.push(requestId)
    }

    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: 'orderCancellation-dismissed-banners',
      value: JSON.stringify(dismissedBanners)
    })

    return JSON.parse(response.data.value)
  },
  dismissFailedOrderChangeRequest: async ({requestId}) => {
    // Get current dismissed failed requests
    const dismissedRequests = await queries.dismissedFailedOrderChangeRequests()

    // Add the new requestId if not already present
    if (!dismissedRequests.includes(requestId)) {
      dismissedRequests.push(requestId)
    }

    // Persist the updated list
    const response = await api.post<DistributedCacheResponse>('distributeduserstorage', {
      keyIdentifier: 'orderChange-dismissed-failed-requests',
      value: JSON.stringify(dismissedRequests)
    })

    return JSON.parse(response.data.value)
  },
  validateConfirmationPin: async ({customerId, pin}) => {
    const response = await api.post<ValidateConfirmationPinResponse>(
      `/customers/${customerId}/validateConfirmationPin`,
      {pin: pin.trim()}
    )
    return response.data
  }
}
